import React, { useEffect, useState, useCallback } from "react";
import axiosInstance from "../api/axiosInstance";
import {
  Building2,
  CheckCircle2,
  Clock,
  AlertTriangle,
  XCircle,
  HelpCircle,
  RefreshCw,
  Download,
} from "lucide-react";
import MainLayout from "../layouts/MainLayout";



// Central place to control how each status looks — badge colors,
// summary-card colors, and the icon shown for it.
const STATUS_STYLES = {
  Ok: {
    badge: "bg-green-100 text-green-700",
    card: "bg-green-50 border-green-200 text-green-700",
    icon: CheckCircle2,
  },
  Delayed: {
    badge: "bg-amber-100 text-amber-700",
    card: "bg-amber-50 border-amber-200 text-amber-700",
    icon: Clock,
  },
  Critical: {
    badge: "bg-red-100 text-red-700",
    card: "bg-red-50 border-red-200 text-red-700",
    icon: AlertTriangle,
  },
  Failed: {
    badge: "bg-rose-100 text-rose-800",
    card: "bg-rose-50 border-rose-200 text-rose-800",
    icon: XCircle,
  },
  "Not Received": {
    badge: "bg-slate-200 text-slate-600",
    card: "bg-slate-50 border-slate-200 text-slate-600",
    icon: HelpCircle,
  },
};

const getStatusStyle = (status) =>
  STATUS_STYLES[status] || {
    badge: "bg-slate-100 text-slate-600",
    card: "bg-slate-50 border-slate-200 text-slate-600",
    icon: HelpCircle,
  };

const formatDateTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatAge = (minutes) => {
  if (!minutes || minutes <= 0) return "—";
  if (minutes < 60) return `${minutes}m ago`;
  const hrs = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hrs < 24) return `${hrs}h ${mins}m ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ${hrs % 24}h ago`;
};

const SUMMARY_CARDS = [
  { key: "totalOutlets", label: "Total Outlets", icon: Building2, style: "bg-slate-50 border-slate-200 text-slate-700" },
  { key: "ok", label: "Ok", icon: CheckCircle2, style: STATUS_STYLES.Ok.card },
  { key: "delayed", label: "Delayed", icon: Clock, style: STATUS_STYLES.Delayed.card },
  { key: "critical", label: "Critical", icon: AlertTriangle, style: STATUS_STYLES.Critical.card },
  { key: "failed", label: "Failed", icon: XCircle, style: STATUS_STYLES.Failed.card },
  { key: "notReceived", label: "Not Received", icon: HelpCircle, style: STATUS_STYLES["Not Received"].card },
];

const Dashboard = () => {
  const [outlets, setOutlets] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastRefreshed, setLastRefreshed] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [dashboardRes, summaryRes] = await Promise.all([
        axiosInstance.get("/BackupDashboard/GetDashboard"),
        axiosInstance.get("/BackupDashboard/Summary"),
      ]);

      setOutlets(dashboardRes.data || []);
      setSummary(summaryRes.data || null);
      setLastRefreshed(new Date());
    } catch (err) {
      console.error(err);
      setError("Unable to load dashboard data. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();

    // Auto-refresh every 60s so the dashboard stays live without a manual click
    const interval = setInterval(loadData, 60000);
    return () => clearInterval(interval);
  }, [loadData]);

  return (
    <MainLayout>
      <div className="flex flex-col h-full p-3 md:p-6 gap-4">
        {/* Page header */}
        <div className="flex-shrink-0 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-800">
              Dashboard
            </h1>
            <p className="text-gray-500 text-sm mt-1">
              Welcome to Backup Management System
            </p>
          </div>

          <div className="flex items-center gap-3">
            {lastRefreshed && (
              <span className="text-xs text-slate-400">
                Updated {lastRefreshed.toLocaleTimeString("en-IN")}
              </span>
            )}
            <button
              onClick={loadData}
              disabled={loading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white rounded-lg transition"
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
          </div>
        </div>

        {error && (
          <div className="flex-shrink-0 px-4 py-2 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
            {error}
          </div>
        )}

        {/* Summary cards */}
        <div className="flex-shrink-0 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {SUMMARY_CARDS.map(({ key, label, icon: Icon, style }) => (
            <div
              key={key}
              className={`rounded-xl border p-3 flex items-center gap-3 ${style}`}
            >
              <Icon size={22} className="shrink-0" />
              <div className="leading-tight">
                <div className="text-xl font-bold">
                  {summary ? summary[key] ?? 0 : "—"}
                </div>
                <div className="text-xs opacity-80">{label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Outlet status table */}
        <div className="flex flex-col flex-1 min-h-0 bg-white rounded-xl shadow-sm border border-slate-200">
          <div className="flex-shrink-0 h-[56px] flex items-center justify-between px-4 md:px-6 border-b">
            <h2 className="text-base md:text-lg font-semibold text-slate-800">
              Outlet Backup Status
            </h2>
            <span className="text-xs text-slate-400">
              {outlets.length} outlet{outlets.length !== 1 ? "s" : ""}
            </span>
          </div>

          <div className="flex-1 min-h-0 overflow-auto scrollbar-hide">
            <table className="w-full min-w-[820px] border-collapse text-xs">
              <thead className="sticky top-0 z-10">
                <tr className="bg-slate-100 border-b border-slate-300">
                  <th className="px-2 py-1.5 text-left font-semibold border border-slate-200">
                    Outlet ID
                  </th>
                  <th className="px-2 py-1.5 text-left font-semibold border border-slate-200">
                    Outlet Name
                  </th>
                  <th className="px-2 py-1.5 text-left font-semibold border border-slate-200">
                    Last Backup Time
                  </th>
                  <th className="px-2 py-1.5 text-right font-semibold border border-slate-200">
                    File Size (MB)
                  </th>
                  <th className="px-2 py-1.5 text-center font-semibold border border-slate-200">
                    Age
                  </th>
                  <th className="px-2 py-1.5 text-center font-semibold border border-slate-200">
                    Status
                  </th>
                  <th className="px-2 py-1.5 text-center font-semibold border border-slate-200">
                    File
                  </th>
                </tr>
              </thead>

              <tbody>
                {loading && outlets.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-8 text-slate-500 border border-slate-200">
                      Loading...
                    </td>
                  </tr>
                ) : outlets.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-8 text-slate-500 border border-slate-200">
                      No Records Found
                    </td>
                  </tr>
                ) : (
                  outlets.map((item, idx) => {
                    const { badge } = getStatusStyle(item.backupStatus);
                    return (
                      <tr
                        key={item.outletId}
                        className={`border-b border-slate-200 hover:bg-slate-50 transition ${
                          idx % 2 === 1 ? "bg-slate-50/50" : "bg-white"
                        }`}
                      >
                        <td className="px-2 py-1 border border-slate-200 whitespace-nowrap">
                          {item.outletId}
                        </td>
                        <td className="px-2 py-1 border border-slate-200 whitespace-nowrap">
                          {item.outletName}
                        </td>
                        <td className="px-2 py-1 border border-slate-200 whitespace-nowrap">
                          {formatDateTime(item.lastBackupTime)}
                        </td>
                        <td className="px-2 py-1 border border-slate-200 text-right whitespace-nowrap">
                          {item.lastFileSizeMB ? item.lastFileSizeMB.toFixed(2) : "0.00"}
                        </td>
                        <td className="px-2 py-1 border border-slate-200 text-center whitespace-nowrap">
                          {formatAge(item.minutesOld)}
                        </td>
                        <td className="px-2 py-1 border border-slate-200 text-center">
                          <span
                            className={`px-2 py-0.5 rounded-full text-[11px] font-medium whitespace-nowrap ${badge}`}
                          >
                            {item.backupStatus}
                          </span>
                        </td>
                        <td className="px-2 py-1 border border-slate-200 text-center">
                          {item.fileExists && item.downloadUrl ? (
                            <a
                              href={item.downloadUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center justify-center p-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition"
                              title="Download backup file"
                            >
                              <Download size={14} />
                            </a>
                          ) : (
                            <span className="text-slate-300">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <style>{`
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </MainLayout>
  );
};

export default Dashboard;